import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Radio, Input, Button } from "semantic-ui-react";
import {
    SetCCReturnedBranches,
    SetCCSensitivityThreshold,
    SetToggleLoopFlow,
    SetLoopFlowThreshold,
    SetCCRedispatching,
    SetMNECsForOptimization,
    SetManualPSTs,
    SetManualTopoRAs,
    SetCCValidationThreshold,
    SetRcdcFix,
    SetMaxRdrUp,
    SetMaxRdrDown,
    SetAllRdSwitchable,
    SetCCIvaThreshold,
    SetCCUseMinRamForIva,
    SetCCIvaMinRamPercentage,
    SetRangeFromInitial,
    SetDefaultDownwards,
    SetDefaultUpwards,
} from "../../../../../actions/ccValidationActions";
import { AppState } from "../../../../../store/configureStore";
import { PstTableModal } from "./PstTableModal";

export const CCValidationRegionOptions = () => {
    const dispatch = useDispatch();
    const ccValidation = useSelector((state: AppState) => state.ccValidation);
    const user = useSelector((state: AppState) => state.user);
    const [isTableModalOpen, setIsTableModalOpen] = useState<boolean>(false);

    return (
        <>
            <h3 id="h3-CCValidation-region-options-basicSettings">Basic Settings</h3>
            <div id="div-CCValidation-region-options-a" className="cc-validation-upper-part bordered">
                <div id="div-CCValidation-region-options-b" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-returnedBranches">Returned branches</p>
                    <Radio
                        toggle
                        name="returnedBranches"
                        checked={ccValidation.returnedBranches}
                        onChange={() =>
                            dispatch(SetCCReturnedBranches(!ccValidation.returnedBranches))
                        }
                    />
                </div>
                <div id="div-CCValidation-region-options-c" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-sensitivityThreshold">Sensitivity threshold (%)</p>
                    <Input
                        id="input-CCValidation-region-options-c"
                        name="sensitivityThreshold"
                        pattern="^-?[0-9]\d*\.?\d*$"
                        className="text-input"
                        type="tel"
                        value={ccValidation.sensitivityThreshold}
                        onChange={(e: any) => {
                            if (
                                e.target.validity.valid &&
                                (e.target.value === "" ||
                                    (parseInt(e.target.value) >= 0 &&
                                        parseInt(e.target.value) <= 100))
                            )
                                dispatch(SetCCSensitivityThreshold(e.target.value));
                        }}
                    />
                </div>
                {(user.role === "Admin" || user.role === "SuperAdmin" || user.tso === "PSE") && (
                    <>
                        <div id="div-CCValidation-region-options-d" className="cc-validation-input">
                            <p id="p-CCValidation-region-options-loopFlow">Loop flow constraint</p>
                            <Radio
                                toggle
                                name="toggleLoopFlow"
                                checked={ccValidation.toggleLoopFlow}
                                onChange={() =>
                                    dispatch(SetToggleLoopFlow(!ccValidation.toggleLoopFlow))
                                }
                            />
                        </div>
                        <div id="div-CCValidation-region-options-e" className="cc-validation-input">
                            <p id="p-CCValidation-region-options-loopThreshold">Loop flow threshold [MW]</p>
                            <Input
                                id="input-CCValidation-region-options-d"
                                name="loopFlowThreshold"
                                pattern="^-?[0-9]\d*\.?\d*$"
                                className="text-input"
                                type="tel"
                                value={ccValidation.loopFlowThreshold}
                                disabled={!ccValidation.toggleLoopFlow}
                                onChange={(e: any) =>
                                    dispatch(SetLoopFlowThreshold(e.target.value))
                                }
                            />
                        </div>
                    </>
                )}
                <div id="div-CCValidation-region-options-f" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-CC-validation">CC validation threshold [MW]</p>
                    <Input
                        id="input-CCValidation-region-options-e"
                        name="validationThreshold"
                        pattern="^-?[0-9]\d*\.?\d*$"
                        className="text-input"
                        type="tel"
                        value={ccValidation.validationThreshold}
                        onChange={(e: any) => dispatch(SetCCValidationThreshold(e.target.value))}
                    />
                </div>
                <div id="div-CCValidation-region-options-ll" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-CC-validation">IVA threshold [MW]</p>
                    <Input
                        id="input-CCValidation-region-options-ff"
                        name="ivaThreshold"
                        pattern="^-?[0-9]\d*\.?\d*$"
                        className="text-input"
                        type="tel"
                        value={ccValidation.ivaThreshold}
                        onChange={(e: any) => dispatch(SetCCIvaThreshold(e.target.value))}
                    />
                </div>
                <div id="div-CCValidation-region-options-dd" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-UseMinRamForIva">Use Min Ram For IVA</p>
                    <Radio
                        toggle
                        name="useMinRamForIva"
                        checked={ccValidation.useMinRamForIva}
                        /*
                        disabled={validationProcess !== ValidationProcess.DayAhead}
                        */
                        onChange={(e, { checked }) => {
                            if (!checked) {
                                checked = false;
                            }
                            dispatch(SetCCUseMinRamForIva(checked));
                        }}
                    />
                </div>
                <div id="div-CCValidation-region-options-ee" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-IvaMinRamPercentage">IVA Min Ram Percentage</p>
                    <Input
                        id="input-CCValidation-region-options-dd"
                        name="IvaMinRamPercentage"
                        pattern="^-?[0-9]\d*\.?\d*$"
                        className="text-input"
                        type="tel"
                        value={ccValidation.ivaMinRamPercentage}
                        /*
                        disabled={validationProcess === ValidationProcess.IntraDay || !ccValidation.useMinRamForIva}
                        */
                        disabled={!ccValidation.useMinRamForIva}
                        onChange={(e: any) =>
                            dispatch(SetCCIvaMinRamPercentage(e.target.value))
                        }
                    />
                </div>
            </div>
            <h3 id="h3-CCValidation-region-options-remedial">Remedial Action Optimization</h3>
            <div id="div-CCValidation-region-options-g" className="cc-validation-upper-part bordered">
                <div id="div-CCValidation-region-options-h" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-redispatching">Redispatching</p>
                    <Radio
                        toggle
                        name="redispatching"
                        checked={ccValidation.redispatching}
                        onChange={() => dispatch(SetCCRedispatching(!ccValidation.redispatching))}
                    />
                </div>
                <div id="div-CCValidation-region-options-i" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-MNECs">MNECs for optimization</p>
                    <Radio
                        toggle
                        name="mnecsForOptimization"
                        checked={ccValidation.mnecsForOptimization}
                        onChange={() =>
                            dispatch(SetMNECsForOptimization(!ccValidation.mnecsForOptimization))
                        }
                    />
                </div>
                <div id="div-CCValidation-region-options-j" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-manualPST">Manual PSTs</p>
                    <Radio
                        toggle
                        name="manualPSTs"
                        checked={ccValidation.manualPSTs}
                        onChange={() => dispatch(SetManualPSTs(!ccValidation.manualPSTs))}
                    />
                </div>
                <div id="div-CCValidation-region-options-k" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-manualTopoRAs">Manual TopoRAs</p>
                    <Radio
                        toggle
                        name="manualTopoRAs"
                        checked={ccValidation.manualTopoRAs}
                        onChange={() => dispatch(SetManualTopoRAs(!ccValidation.manualTopoRAs))}
                    />
                </div>
                <div id="pst-table-options-rangeFromInitial" className="cc-validation-input">
                    <p>Range from initial</p>
                    <Radio
                        toggle
                        name="rangeFromInitial"
                        checked={ccValidation.rangeFromInitial}
                        onChange={() => dispatch(SetRangeFromInitial(!ccValidation.rangeFromInitial))}
                    />
                </div>
                <div id="pst-table-options-defaultDownwards" className="cc-validation-input">
                    <p>Default downwards</p>
                    <Input
                        id="input-CCValidation-region-options-defaultDownwards"
                        name="defaultDownwards"
                        pattern="^-?[0-9]\d*\.?\d*$"
                        className="text-input"
                        type="number"
                        value={ccValidation.defaultDownwards}
                        onChange={(e: any) => {
                            if (e.target.validity.valid || e.target.value === "") { dispatch(SetDefaultDownwards(e.target.value)) };
                        }}
                        disabled={!ccValidation.rangeFromInitial} />
                </div>
                <div id="pst-table-options-defaultUpwards" className="cc-validation-input">
                    <p>Default upwards</p>
                    <Input
                        id="input-CCValidation-region-options-defaultUpwards"
                        name="defaultUpwards"
                        pattern="^-?[0-9]\d*\.?\d*$"
                        className="text-input"
                        type="number"
                        value={ccValidation.defaultUpwards}
                        onChange={(e: any) => {
                            if (e.target.validity.valid || e.target.value === "") { dispatch(SetDefaultUpwards(e.target.value)) };
                        }}
                        disabled={!ccValidation.rangeFromInitial} />
                </div>
                <div id="div-CCValidation-region-options-modal-button" className="cc-validation-input">
                    <p>{String.fromCharCode(160)}</p>
                    <Button
                        onClick={() => setIsTableModalOpen(!isTableModalOpen)}
                        disabled={!ccValidation.rangeFromInitial}
                    >Manage PST ranges
                    </Button>
                </div>
            </div>
            <h3 id="h3-CCValidation-region-options-Deficit">Redispatching Compensation Deficit</h3>
            <div id="div-CCValidation-region-options-l" className="cc-validation-upper-part bordered">
                <div id="div-CCValidation-region-options-m" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-z">&nbsp;</p>
                    <div id="div-CCValidation-region-options-n" className="double-labeled-selector">
                        <p id="p-CCValidation-region-options-error">ERROR</p>
                        <Radio
                            toggle
                            name="fix"
                            checked={ccValidation.rcdcFix}
                            onChange={() => dispatch(SetRcdcFix(!ccValidation.rcdcFix))}
                        />
                        <p id="p-CCValidation-region-options-fix">FIX</p>
                    </div>
                </div>
                <div id="div-CCValidation-region-options-o" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-rdr-up">MAX_RDR_UP [p.u]</p>
                    <Input
                        id="input-CCValidation-region-options-maxRcdcUp"
                        name="maxRcdcUp"
                        pattern="^-?[0-9]\d*\.?\d*$"
                        className="text-input"
                        type="tel"
                        value={ccValidation.maxRdrUp}
                        onChange={(e: any) => {
                            if (e.target.validity.valid || e.target.value === "")
                                dispatch(SetMaxRdrUp(e.target.value));
                        }}
                        disabled={!ccValidation.rcdcFix}
                    />
                </div>
                <div id="div-CCValidation-region-options-p" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-rdr-down">MAX_RDR_DOWN [p.u]</p>
                    <Input
                        id="input-CCValidation-region-options-maxRcdcDown"
                        name="maxRcdcDown"
                        pattern="^-?[0-9]\d*\.?\d*$"
                        className="text-input"
                        type="tel"
                        value={ccValidation.maxRdrDown}
                        onChange={(e: any) => {
                            if (e.target.validity.valid || e.target.value === "")
                                dispatch(SetMaxRdrDown(e.target.value));
                        }}
                        disabled={!ccValidation.rcdcFix}
                    />
                </div>
                <div id="div-CCValidation-region-options-r" className="cc-validation-input">
                    <p id="p-CCValidation-region-options-allRD">All RD Switchable</p>
                    <Radio
                        toggle
                        name="allRdSwitchable"
                        checked={ccValidation.allRdSwitchable}
                        onChange={() => dispatch(SetAllRdSwitchable(!ccValidation.allRdSwitchable))}
                    />
                </div>
            </div>
            <div id="div-CCValidation-region-options-pst-table-modal">
                <PstTableModal isOpen={isTableModalOpen} setIsOpen={setIsTableModalOpen} />
            </div>
        </>
    );
};
